import { useState, type ReactNode } from 'react';
import { CampoTexto } from '../../components/ui.js';
import { addDays, formatLongDate, startOfWeek, todayLocal } from '../../lib/dates.js';

/**
 * Elegir el día de comienzo del bloque (§2.3 de TRAINING_ENGINE.md).
 *
 * POR QUÉ NO ES UN CAMPO DE FECHA A SECAS
 *
 * El sistema entero cuenta por **semanas de lunes a domingo**: las sesiones se
 * colocan como desplazamientos desde `weekStart`, la revisión semanal agrupa
 * por ahí y la nutrición reparte el día dentro de esa semana. Así que una fecha
 * elegida no se usa tal cual: `createBlockForPhase` la lleva al lunes de su
 * semana.
 *
 * Callarse eso convertiría «empiezo el miércoles 10» en un bloque que arranca
 * el lunes 8, con dos sesiones que nacen en el pasado y que la primera revisión
 * marca como perdidas. Por eso aquí se enseña **el lunes resultante**, no la
 * fecha tecleada, y se avisa cuando esa semana ya ha empezado.
 *
 * Esto último no se prohíbe: empezar a mitad de semana es una decisión legítima
 * —se entrena lo que queda— y el motor lo soporta. Lo que no puede pasar es que
 * ocurra sin que nadie lo haya dicho.
 */

/** El lunes en el que empezaría de verdad un bloque pedido para `fecha`. */
export function lunesDeArranque(fecha: string): string {
  return startOfWeek(fecha);
}

/**
 * El valor por defecto: **esta semana**, que es lo que la pantalla hacía ya.
 *
 * Se mantiene a propósito. Poner el próximo lunes por defecto sería más
 * limpio —nada nacería en el pasado— pero cambiaría el comportamiento de todo
 * el mundo para resolver algo que nadie pidió, y haría esperar seis días a
 * quien configure la aplicación un martes y quiera entrenar hoy.
 *
 * Lo que sí cambia es que **ahora se dice**: si la semana ya empezó, el aviso
 * lo explica y el próximo lunes está a un toque. La opción es del usuario; la
 * pantalla solo deja de callarse la consecuencia.
 */
export function lunesPorDefecto(hoy = todayLocal()): string {
  return startOfWeek(hoy);
}

/** El lunes siguiente al de `fecha`. Para el atajo del aviso. */
export function lunesSiguiente(fecha: string): string {
  return addDays(startOfWeek(fecha), 7);
}

export function CuandoEmpezar({
  valor,
  onChange,
  deshabilitado,
}: {
  valor: string;
  onChange: (fecha: string) => void;
  deshabilitado: boolean;
}): ReactNode {
  const [hoy] = useState(() => todayLocal());
  const lunes = lunesDeArranque(valor);
  const yaEmpezada = lunes < startOfWeek(hoy);
  const enCurso = lunes === startOfWeek(hoy) && lunes !== hoy;

  return (
    <>
      <CampoTexto
        etiqueta="¿Qué día quieres empezar?"
        type="date"
        value={valor}
        // Nada de semanas ya cerradas: un bloque que arranca en el pasado nace
        // con todas sus sesiones perdidas y sin nada que se pueda entrenar.
        min={startOfWeek(hoy)}
        disabled={deshabilitado}
        onChange={(evento) => onChange(evento.target.value)}
        pista="El plan se organiza por semanas de lunes a domingo."
      />

      {valor !== '' && !yaEmpezada && (
        <p className="campo__pista" role="status">
          El bloque empezará el <strong>{formatLongDate(lunes)}</strong>.
          {enCurso && (
            <>
              {' '}
              Esa semana ya ha empezado: las sesiones anteriores a hoy contarán como perdidas y la
              revisión propondrá recolocarlas.{' '}
              <button
                type="button"
                className="boton boton--texto"
                disabled={deshabilitado}
                onClick={() => onChange(lunesSiguiente(valor))}
              >
                Empezar el lunes que viene
              </button>
            </>
          )}
        </p>
      )}

      {yaEmpezada && (
        <p className="campo__error" role="alert">
          Esa semana ya terminó. Elige una fecha de esta semana en adelante.
        </p>
      )}
    </>
  );
}
